import { useState } from 'react';
import { useLanguage } from '../useLanguage';

const Workshops = () => {
  const { t } = useLanguage();
  const [expandedWorkshop, setExpandedWorkshop] = useState<number | null>(null);

  const workshops = t.workshops.workshopsList.map((workshop, index) => ({
    icon: ['🎓', '🔒', '☁️', '🎯'][index],
    title: workshop.title,
    summary: workshop.summary,
    topics: workshop.topics,
    duration: workshop.duration,
    level: workshop.level,
    color: ['from-secondary-700 to-secondary-800', 'from-dark-700 to-secondary-700', 'from-primary-700 to-primary-800', 'from-secondary-800 to-dark-800'][index],
  }));

  return (
    <section id="workshops" tabIndex={-1} className="scroll-mt-16 section-padding bg-white dark:bg-dark-900">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t.workshops.title}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {t.workshops.subtitle}
          </p>
          <a
            href="https://calendly.com/benjamin-juarez/60min"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary mt-8 inline-flex items-center justify-center gap-2"
          >
            <span>{t.workshops.scheduleWorkshop}</span>
            <span aria-hidden="true">→</span>
          </a>
        </div>

        <div className="grid items-start md:grid-cols-2 gap-8 mb-16">
          {workshops.map((workshop, index) => (
            <article
              key={index}
              className="card p-6"
            >
              <div className="text-center mb-6">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r ${workshop.color} flex items-center justify-center text-3xl shadow-lg`}>
                  {workshop.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {workshop.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {workshop.summary}
                </p>
                <div className="flex flex-wrap justify-center gap-3 text-sm">
                  <span className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full">
                    {workshop.duration}
                  </span>
                  <span className="px-3 py-1 bg-secondary-100 dark:bg-secondary-900/30 text-secondary-700 dark:text-secondary-300 rounded-full">
                    {workshop.level}
                  </span>
                  <span className="px-3 py-1 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-200 rounded-full">
                    {t.workshops.inPerson}
                  </span>
                </div>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  className="min-h-11 px-3 inline-flex items-center justify-center gap-2 text-primary-700 dark:text-primary-300 hover:underline font-medium rounded-md"
                  aria-expanded={expandedWorkshop === index}
                  aria-controls={`workshop-details-${index}`}
                  onClick={() => setExpandedWorkshop(expandedWorkshop === index ? null : index)}
                >
                  <span>{expandedWorkshop === index ? t.workshops.showLess : t.workshops.learnMore}</span>
                  <span className="accordion-chevron" aria-hidden="true">↓</span>
                </button>
              </div>

              <div
                id={`workshop-details-${index}`}
                className="accordion-content"
                data-expanded={expandedWorkshop === index}
                aria-hidden={expandedWorkshop !== index}
              >
                <div className="accordion-content-inner">
                  <div className="space-y-3 pt-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white">{t.workshops.keyTopics}</h4>
                    <ul className="space-y-2">
                      {workshop.topics.map((topic, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <span className="text-green-500 mt-1">✓</span>
                          <span className="text-gray-700 dark:text-gray-300 text-sm">
                            {topic}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="text-center">
          <div className="bg-gradient-to-r from-primary-700 to-secondary-700 text-white p-8 rounded-2xl shadow-xl">
            <h3 className="text-2xl font-bold mb-4">{t.workshops.readyToTransform}</h3>
            <p className="text-lg mb-6 opacity-90 max-w-2xl mx-auto">
              {t.workshops.transformDesc}
            </p>
            <a
              href="https://calendly.com/benjamin-juarez/60min"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-primary-700 hover:bg-gray-100 font-bold py-3 px-8 rounded-lg transition-[background-color,transform] duration-200 hover:scale-105 active:scale-[0.98] inline-flex items-center justify-center space-x-2"
            >
              <span>{t.workshops.scheduleWorkshop}</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Workshops;
